
































































































































































































































































































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import { mapState, mapGetters } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import HasValidation from "@/mixins/HasValidation";
import AdminBrandId from "@/mixins/adminBrandId";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { TranslationsTypes } from "@/data/enums/translations";

// Typings
import {
  ITicketDepartment,
  ISupportDepartmentBasicForm,
  ISupportDepartmentAssignmentForm
} from "@/models/tickets";
import { IBrand } from "@/models/brands";
import { IState } from "@/store";

@Component({
  computed: {
    ...mapState({
      supportDepartmentScope(state: IState, getters) {
        return getters[`data/${DataModules.SUPPORT_DEPARTMENTS}/scope`](
          this.supportDepartmentId
        );
      },
      supportDepartment(state: IState, getters) {
        return (
          getters[`data/single`]({
            storeModule: DataModules.SUPPORT_DEPARTMENTS,
            scope: this.supportDepartmentScope
          }) || null
        );
      }
    }),
    ...mapGetters({
      hasMultipleBrands: "auth/admin/hasMultipleBrands"
    })
  },
  components: {
    "assigned-brands-lists": () =>
      import(
        "@/components/app/admin/settings/supportDepartments/assignedBrandsLists.vue"
      )
  }
})
export default class ManageSupportDepartmentModal extends Mixins(
  HasValidation,
  AdminBrandId
) {
  // Refs

  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    name: HTMLFormElement;
  };

  // Map state

  public supportDepartmentScope!: string;
  public supportDepartment!: ITicketDepartment;
  public hasMultipleBrands!: boolean;

  // Props

  @Prop({ default: null })
  public supportDepartmentId!: ITicketDepartment;

  @Prop({ default: true })
  public showBasicConfiguration!: boolean;

  @Prop({ default: null })
  public usedBrand!: IBrand;

  // Data

  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;

  public basicForm: ISupportDepartmentBasicForm = {
    name: "",
    code: ""
  };
  public basicFormClone: ISupportDepartmentBasicForm = _.clone(this.basicForm);

  public assignmentForm: ISupportDepartmentAssignmentForm = {
    name: "",
    username: null,
    default: false,
    is_public: true
  };
  public assignmentFormClone: ISupportDepartmentAssignmentForm = _.clone(
    this.assignmentForm
  );

  public isAssignedValue = false;
  public isAssignedValueClone = false;

  // Life-cycle methods

  public created() {
    if (!this.contentIsAvailable) return;

    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.SUPPORT_DEPARTMENTS,
      scope: this.supportDepartmentScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    if (this.isCreate) {
      this.isLoading = false;
      return;
    }

    try {
      await this.getSupportDepartmentData(this.isReloading);

      this.initiateForms();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getSupportDepartmentData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.SUPPORT_DEPARTMENTS}/get`, {
      id: this.supportDepartmentId,
      scope: this.supportDepartmentScope,
      vm: this,
      ignoreStored
    });
  }

  public initiateForms() {
    if (this.isCreate) return;

    this.isAssignedValue = this.isAssigned;
    this.isAssignedValueClone = _.clone(this.isAssignedValue);

    this.basicForm = _.pick(this.supportDepartment, ["name", "code"]);
    this.basicFormClone = _.clone(this.basicForm);

    if (_.isNull(this.brandAssignment)) return;

    this.assignmentForm = _.pick(this.brandAssignment, [
      "name",
      "username",
      "default",
      "is_public"
    ]) as ISupportDepartmentAssignmentForm;
    this.assignmentFormClone = _.clone(this.assignmentForm);
  }

  public async validateBeforeSubmit() {
    if (!this.canManageDepartment) return;

    this.isProcessing = true;

    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      await Promise.all([
        this.saveBasicConfiguration(),
        this.saveBrandAssignment()
      ]);

      this.$bus.$emit("RefreshSupportDepartments");

      this.$store.dispatch("toast/show", {
        message: this.isCreate
          ? this.$t("_sentence.support.dept_successfully_created")
          : this.$t("_sentence.support.dept_successfully_updated")
      });

      this.$emit("success");
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public async saveBasicConfiguration() {
    if (!_.isNull(this.usedBrand)) return Promise.resolve();

    return this.$store.dispatch(
      `data/${DataModules.SUPPORT_DEPARTMENTS}/${
        this.isCreate ? "create" : "update"
      }`,
      _.merge(
        {
          data: this.getNewValues(this.basicForm, this.basicFormClone)
        },
        this.isCreate ? {} : { id: this.supportDepartment.id }
      )
    );
  }

  public async saveBrandAssignment() {
    if (this.isCreate || (this.isOrgMode && _.isNull(this.usedBrand)))
      return Promise.resolve();

    if (this.isAssigned && !this.isAssignedValue)
      return this.unassignBrand(
        _.get(this.brandAssignment, "id", ""),
        this.usedBrand?.id || this.brandId
      );

    if (this.isAssigned) {
      return this.$store.dispatch(
        `data/${DataModules.SUPPORT_DEPARTMENTS_BRAND}/update`,
        {
          data: {
            ...this.getNewValues(this.assignmentForm, this.assignmentFormClone),
            brand_id: this.usedBrand?.id || this.brandId
          },
          supportDepartmentId: _.get(this.brandAssignment, "id", "")
        }
      );
    }

    return this.assignBrand({
      ...(_.isNull(this.assignmentForm.username)
        ? _.omit(this.assignmentForm, ["username"])
        : this.assignmentForm),
      brand_id: this.usedBrand?.id || this.brandId,
      ticket_department_id: this.supportDepartment.id
    });
  }

  public async switchAssignment(departmentAssignment) {
    const isAssigned = !_.isNull(departmentAssignment.department);

    try {
      this.isProcessing = true;

      if (isAssigned)
        await this.unassignBrand(
          departmentAssignment.department.id,
          departmentAssignment.department.brand_id
        );
      else
        await this.assignBrand({
          brand_id: departmentAssignment.brand.id,
          ticket_department_id: this.supportDepartment.id
        });

      this.$bus.$emit("RefreshSupportDepartments");
      await this.reloadData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public async unassignBrand(supportDepartmentId, brandId) {
    return this.$store.dispatch(
      `data/${DataModules.SUPPORT_DEPARTMENTS_BRAND}/unassign`,
      {
        supportDepartmentId,
        brandId
      }
    );
  }

  public assignBrand(data) {
    return this.$store.dispatch(
      `data/${DataModules.SUPPORT_DEPARTMENTS_BRAND}/assign`,
      { data }
    );
  }

  public openTranslateModal() {
    if (!this.canShowTranslations) return;

    this.$store.dispatch(
      `data/${DataModules.TRANSLATIONS}/openTranslationsModal`,
      {
        props: {
          brandId: this.brandAssignment.brand_id,
          fieldName: "name",
          fieldLabel: this.$t("_.display_name"),
          objectId: this.brandAssignment.id,
          objectTypeCode: TranslationsTypes.BRAND_TICKET_DEPARTMENT,
          fieldType: "text"
        }
      }
    );
  }

  public formatValue(value) {
    return value
      .toLowerCase()
      .trim()
      .replace(/\s/g, "-")
      .replace(/[-]{2,}/g, "-");
  }

  public nullify(key, val, vm: any = this) {
    const value = val && val.length ? val : null;
    this.$set(vm, key, value);
  }

  public copyEmailToClipboard() {
    return this.$copyToClipboard(
      [this.assignmentForm.username || "", "@", this.mailboxHostname].join("")
    );
  }

  // Computed

  public get isCreate() {
    return _.isNull(this.supportDepartmentId);
  }

  public get isAssigned() {
    return !_.isNull(this.brandAssignment);
  }

  public get brandAssignment(): ITicketDepartment {
    return (_.find(this.supportDepartment?.brand_ticket_departments || [], [
      "brand_id",
      this.usedBrand?.id || this.brandId
    ]) || null) as ITicketDepartment;
  }

  public get brandName() {
    return this.usedBrand?.name || this.$store.getters["brand/name"];
  }

  public get formIsChanged() {
    return (
      !_.isEqual(this.basicForm, this.basicFormClone) ||
      !_.isEqual(this.assignmentForm, this.assignmentFormClone) ||
      !_.isEqual(this.isAssignedValue, this.isAssignedValueClone)
    );
  }

  public get formIsDisabled() {
    return (
      this.isProcessing ||
      !this.formIsChanged ||
      this.hasErrors ||
      !this.canManageDepartment
    );
  }

  public getNewValues(form, formClone) {
    if (this.isCreate) return form;

    return _.omitBy(form, (v, k) => {
      return formClone[k] === v;
    });
  }

  public get mailboxHostname() {
    return `${this.usedBrand?.code || this.$store.state.brand.code}.${
      process.env.VUE_APP_UPMIND_APP_DOMAIN
    }`;
  }

  public get canManageDepartment() {
    return this.isCreate
      ? this.$userCan("create_tickets_departments")
      : this.$userCan("update_tickets_departments");
  }

  public get canShowTranslations() {
    return (
      !this.isCreate &&
      this.$userCan("create_object_translation") &&
      this.$userCan("get_object_translation") &&
      this.$userCan("list_object_translations")
    );
  }

  // Guarding

  public guardFunctionalities = ["get_tickets_departments"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Watchers

  @Watch("basicForm.name")
  public onNameChange(newValue, oldValue) {
    if (this.basicForm.code === this.formatValue(oldValue)) {
      this.basicForm.code = this.formatValue(newValue);
    }
  }

  @Watch("assignmentForm.default")
  public onDefaultChange() {
    if (!this.assignmentForm.default) {
      return;
    }
    this.assignmentForm.is_public = true;
  }
}
